import { docsRoutes } from "@/lib/navigation-docs";
import { scopeNavigationPreviewRoutes } from "./navigation-preview-routes";
import type { LucideIcon } from "lucide-react";

export { customerFacingRouteIds, routeBuilders } from "./navigation-routes";
export {
  fleetPageForLabel,
  fleetScopeInPath,
  retiredRepositoryInPath,
} from "./navigation-fleet";
import { retiredRouteInPath } from "./navigation-fleet";
import { fleetPageInPath } from "./navigation-fleet";
import { estatesUxRouteInPath } from "./estates-ux";

export type RouteSection =
  | "Fleet"
  | "Analytics"
  | "Standards and Tastes"
  | "Agents"
  | "Autar Docs"
  | "Settings"
  | "";

export type RouteDefinition = {
  id: string;
  path: string;
  section: RouteSection;
  label: string;
  flag?: string;
  live: boolean;
  nav?: boolean;
};

export const routeManifest = [
  {
    id: "account_home",
    path: "/",
    section: "",
    label: "Home",
    live: true,
  },
  {
    id: "repositories",
    path: "/repositories",
    section: "Fleet",
    label: "Repositories",
    flag: "client-nav-repositories",
    live: true,
    nav: true,
  },
  {
    id: "upload_archive",
    path: "/repositories/upload",
    section: "Fleet",
    label: "Upload Archive",
    live: true,
  },
  {
    id: "fleet_agents",
    path: "/services",
    section: "Fleet",
    label: "Fleet Agents",
    flag: "client-nav-fleet-agents",
    live: false,
    nav: true,
  },
  {
    id: "inspection_history",
    path: "/maintenance/inspect",
    section: "Fleet",
    label: "Inspect",
    flag: "client-nav-inspect",
    live: true,
  },
  {
    id: "waste_index",
    path: "/analytics/waste-index",
    section: "Analytics",
    label: "Engineering Waste Index",
    flag: "client-nav-engineering-waste-index",
    live: true,
    nav: true,
  },
  ...scopeNavigationPreviewRoutes,
  {
    id: "sdlc_findings",
    path: "/engineering-waste/map",
    section: "Analytics",
    label: "SDLC Findings",
    flag: "client-nav-sdlc-findings",
    live: true,
    nav: true,
  },
  {
    id: "dependency_tracker",
    path: "/pages/dependency-tracker",
    section: "Analytics",
    label: "Dependency Tracker",
    flag: "client-nav-dependency-tracker",
    live: true,
    nav: true,
  },
  {
    id: "dependency_graph_concept",
    path: "/analytics/dependency-graph-concept",
    section: "Analytics",
    label: "Dependency Graph Concept",
    flag: "client-nav-dependency-graph-concept",
    live: true,
    nav: true,
  },
  {
    id: "feedback_analysis",
    path: "/analytics/feedback-analysis",
    section: "Analytics",
    label: "Feedback Analysis",
    flag: "client-nav-feedback-analysis",
    live: true,
    nav: true,
  },
  {
    id: "commit_history",
    path: "/analytics/commit-history",
    section: "Analytics",
    label: "Commit Intelligence",
    live: true,
    nav: true,
  },
  {
    id: "agent_intelligence",
    path: "/analytics/agent-intelligence",
    section: "Analytics",
    label: "Agent Intelligence",
    live: true,
    nav: true,
  },
  {
    id: "prompt_optimizations",
    path: "/pages/prompt-optimizations",
    section: "Analytics",
    label: "Prompt Optimizations",
    flag: "client-nav-prompt-optimizations",
    live: false,
    nav: false,
  },
  {
    id: "waste_tracking",
    path: "/analytics/waste-tracking",
    section: "Analytics",
    label: "Waste Tracking",
    live: true,
  },
  {
    id: "value_stream_waste_demo",
    path: "/analytics/value-stream-waste",
    section: "Analytics",
    label: "Value Stream Waste",
    flag: "client-nav-value-stream-waste-demo",
    live: false,
    nav: false,
  },
  {
    id: "standards_and_tastes",
    path: "/standards-and-tastes",
    section: "Standards and Tastes",
    label: "Standards and Tastes",
    flag: "client-nav-standards-and-tastes",
    live: true,
    nav: true,
  },
  {
    id: "preference_engine",
    path: "/pages/preference-engine",
    section: "Standards and Tastes",
    label: "Preference Engine",
    flag: "client-nav-preference-engine",
    live: true,
    nav: false,
  },
  {
    id: "suppressed",
    path: "/standards/suppressed-recommendations",
    section: "Standards and Tastes",
    label: "Suppressed",
    live: true,
    nav: false,
  },
  {
    id: "dependency_policy",
    path: "/standards/dependency-policy",
    section: "Standards and Tastes",
    label: "Dependency Policy",
    live: true,
    nav: false,
  },
  {
    id: "agent_health",
    path: "/agents/health",
    section: "Agents",
    label: "Agent Health",
    flag: "client-nav-agent-health",
    live: false,
    nav: false,
  },
  {
    id: "models",
    path: "/pages/models",
    section: "Agents",
    label: "Models",
    flag: "client-nav-models",
    live: false,
    nav: false,
  },
  {
    id: "agents_md_builder",
    path: "/agents/agents-md-builder",
    section: "Standards and Tastes",
    label: "Agent Standards",
    flag: "client-nav-agents-md-builder",
    live: true,
    nav: true,
  },
  {
    id: "docs",
    path: "/docs",
    section: "Autar Docs",
    label: "Autar Docs",
    flag: "client-nav-security-access",
    live: true,
  },
  {
    id: "estate_setup",
    path: "/settings/estate-setup",
    section: "Settings",
    label: "Estate Setup",
    flag: "client-nav-estate-setup",
    live: true,
    nav: false,
  },
  {
    id: "access",
    path: "/settings/access",
    section: "Settings",
    label: "Access",
    flag: "client-nav-access",
    live: true,
    nav: true,
  },
  {
    id: "providers",
    path: "/settings/providers",
    section: "Settings",
    label: "Providers",
    flag: "client-nav-providers",
    live: true,
    nav: true,
  },
  {
    id: "subscriptions",
    path: "/settings/subscriptions",
    section: "Settings",
    label: "Subscriptions",
    flag: "client-nav-subscriptions",
    live: true,
    nav: false,
  },
  {
    id: "notifications",
    path: "/notifications",
    section: "Settings",
    label: "Notifications",
    flag: "client-nav-notifications",
    live: true,
    nav: false,
  },
  {
    id: "api_tokens",
    path: "/settings/api-tokens",
    section: "Settings",
    label: "API Tokens",
    live: true,
    nav: false,
  },
  {
    id: "user_settings",
    path: "/settings/account",
    section: "Settings",
    label: "User Settings",
    live: true,
  },
  {
    id: "admin",
    path: "/admin/scans",
    section: "Settings",
    label: "Admin Dashboard",
    live: true,
  },
  {
    id: "admin_tasks",
    path: "/admin/task-executions",
    section: "Settings",
    label: "Admin Dashboard",
    live: true,
  },
  {
    id: "admin_catalog",
    path: "/admin/maintenance-tasks",
    section: "Settings",
    label: "Admin Dashboard",
    live: true,
  },
  {
    id: "admin_diagnostics",
    path: "/admin/diagnostics",
    section: "Settings",
    label: "Admin Diagnostics",
    live: true,
  },
  {
    id: "cli",
    path: "/cli",
    section: "Settings",
    label: "CLI Guide",
    live: true,
  },
  {
    id: "terms",
    path: "/terms",
    section: "Settings",
    label: "Terms of Use",
    live: true,
  },
  {
    id: "privacy",
    path: "/privacy",
    section: "Settings",
    label: "Privacy Policy",
    live: true,
  },
] as const satisfies readonly RouteDefinition[];

export type RouteId = (typeof routeManifest)[number]["id"];
export type RouteLabel = (typeof routeManifest)[number]["label"];

export { customerFeatureDocs } from "@/lib/navigation-docs";

export const allRouteManifest: readonly RouteDefinition[] = [
  ...routeManifest,
  ...docsRoutes.map(([slug]) => ({
    id: `docs_${slug}`,
    path: `/docs/${slug}`,
    section: "Autar Docs" as const,
    label: `docs:${slug}`,
    live: true,
  })),
];

export type NavigationSection = {
  title: string;
  icon: LucideIcon;
  items: string[];
  /** The heading is the destination: it opens the page named by its title. */
  direct?: boolean;
};

// The three "Admin Dashboard" routes share a label; first registered wins so
// pathForItem lands on /admin/scans, not the last-declared admin tab.
export const itemPaths: Record<string, string> = {};
for (const route of allRouteManifest) {
  if (!(route.label in itemPaths)) itemPaths[route.label] = route.path;
}

// Compatibility aliases are explicit and cannot collide with canonical labels.
Object.assign(itemPaths, {
  Settings: "/settings/estate-setup",
  "Security & Access": "/docs",
  "Roles and permissions": "/docs/roles",
  Fleets: "/fleets",
});

export const navigationFeatureFlags: Record<string, string | undefined> =
  Object.fromEntries(
    routeManifest
      .filter((route) => "nav" in route && route.nav)
      .map((route) => [route.label, "flag" in route ? route.flag : undefined]),
  );

export const mutedItems: Set<string> = new Set(
  routeManifest
    .filter((route) => "nav" in route && route.nav && !route.live)
    .map((route) => route.label),
);

export const drilldownItems = new Set<string>();

const staticRoutesByPath = new Map(
  allRouteManifest.map((route) => [route.path, route]),
);
// First route per label wins, matching itemPaths (see there).
const staticRoutesByLabel = new Map<string, RouteDefinition>();
for (const route of allRouteManifest) {
  if (!staticRoutesByLabel.has(route.label))
    staticRoutesByLabel.set(route.label, route);
}

export type ResolvedRoute = { label: string; section: RouteSection };

// The Fleets page is registered in appShellPaths and is where the fleet
// destination fallbacks send a reader, but it is not a nav destination and so
// has no manifest entry. Without this pair a cold load of /fleets leaves the
// shell on its initial "Home" selection, and pathForItem("Fleets") falls
// through to "/not-found" — a path no route claims.
const fleetChooserRoute = {
  path: "/fleets",
  label: "Fleets",
  section: "Fleet" as RouteSection,
};

// Where the marketing site's View and Add land. Like the Fleets page: a shell
// page with no nav item.
const marketMapRoute = {
  path: "/market-map",
  label: "Market Map",
  section: "Fleet" as RouteSection,
};
itemPaths[marketMapRoute.label] = marketMapRoute.path;

// Creating a campaign is a page of its own, reached from the Campaigns page's
// button rather than the nav, like the Fleets chooser above.
const campaignCreateRoute = {
  path: "/automations/campaigns/new",
  label: "New Campaign",
  section: "Analytics" as RouteSection,
};
itemPaths[campaignCreateRoute.label] = campaignCreateRoute.path;

export function resolveRoute(pathname: string): ResolvedRoute | null {
  const staticRoute =
    staticRoutesByPath.get(pathname) ?? estatesUxRouteInPath(pathname);
  if (staticRoute) return staticRoute;
  if (pathname === fleetChooserRoute.path) return fleetChooserRoute;
  if (pathname === marketMapRoute.path) return marketMapRoute;
  if (pathname === campaignCreateRoute.path) return campaignCreateRoute;
  // Before the retired shapes below: those are only still matched so a link
  // already sent resolves to something the shell can re-point.
  const scoped = fleetPageInPath(pathname);
  if (scoped) return { label: scoped, section: "Fleet" };
  return retiredRouteInPath(pathname);
}

export function pathForItem(item: string) {
  return staticRoutesByLabel.get(item)?.path ?? itemPaths[item] ?? "/not-found";
}
